/**
 * web/src/screens/Settings/index.tsx — the screen the router mounts. The
 * former 1,732-line Settings screen split into section modules (card
 * MudceTexpbq1): this file is orchestration only — it owns the screen's
 * state and the load/save handlers, and passes them to the section modules
 * as props.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isAbort } from '../../shell/bridge';
import { authApi, authErrorMessage, exportMeRequest, getAuthSession } from '../../lib/api';
import { SubTabs, SubPanel } from '../../components/SubTabs';
import { SETTINGS_TABS } from './tabs';
import { YouSection } from './you';
import { UsSection } from './us';
import { ConciergeSection } from './concierge';
import { PlanSection } from './plan';
import { AboutSection } from './about';
import { isCustomKey, MAX_CUSTOM, recapReason } from './helpers';
import type { AccountName, BillingState, CoupleSettings, ExtendedProps, Holiday, Profile, RecapState, Toggles } from './types';
import './settings.css';

export { SETTINGS_TAB_KEYS, settingsTabFromHash } from './tabs';

export function Settings({ onClose, onLeave, onSignOut, onOpenAI, onOpenHelp, onOpenTour, pendingCode, awaitingPartner, mailEnabled, tab, onTabChange }: ExtendedProps) {
  const [toggles, setToggles] = useState<Toggles | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  /** Card ocJz1TeLT4pi — 'Download my data first' lets a leaver keep their
   *  private half before the couple is gone. `exporting` disables the export
   *  button while the bundle is in flight; `exportMsg` reports success/error
   *  in the confirm sheet's status line. */
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  /** Card Vf23eSKKxDhX — Leave is a server call now, so it has a flight of
   *  its own: `leaving` disables the confirm button (one tap, one request),
   *  and `leaveError` carries the server's refusal back into the shared
   *  status line. A refusal keeps `confirming` open — the person is still in
   *  the couple and the reason is worth reading before they try again. */
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [settings, setSettings] = useState<CoupleSettings | null>(null);
  /** The two names (card yFLCjm7ONIEc). `realName` is the account's own
   *  record (D1), `chatName` is what the partner sees in the room. null =
   *  not loaded yet; `nameBlocked` says why the real-name field is not there. */
  const [realName, setRealName] = useState<string | null>(null);
  const [nameBlocked, setNameBlocked] = useState<string | null>(null);
  const [chatName, setChatName] = useState<string | null>(null);
  /** Autosave is per keystroke, so each field tracks its own newest request:
   *  a slow response for an earlier keystroke must not repaint a newer value
   *  or clear the other field's status line. */
  const nameSeq = useRef(0);
  const chatSeq = useRef(0);
  /** Once someone has typed in a name field, a later load() (another row's
   *  failed save reloads everything) must not overwrite what they are in the
   *  middle of writing — that would be the silent rollback this card bans. */
  const nameDirty = useRef(false);
  const chatDirty = useRef(false);
  /** The couple's shared handle (card w8Mj4ThRii6Z). `handle` is what the
   *  server holds, `handleDraft` what is typed but not yet saved — a failed
   *  save keeps the draft in the field, exactly like the name rows. */
  const [handle, setHandle] = useState<string | null>(null);
  const [handleDraft, setHandleDraft] = useState<string | null>(null);
  const handleDirty = useRef(false);
  /** Times a week this member wants a partner digest — 0 is off (card g3ixXFWzhgJY). */
  const [digestFreq, setDigestFreq] = useState<number>(0);
  /** Weekly recap opt-in (card 3UwJvUtiirGN): the 'recap' AI toggle, plus
   *  the window so this row can say when the last one went out and, with
   *  nextDueAt, when the next one may go (card si3nCYgCqe4g). */
  const [recap, setRecap] = useState<RecapState | null>(null);
  const [recapNote, setRecapNote] = useState<string | null>(null);
  /** One send attempt per mount — the window closes server-side, but a
   *  Settings re-render must never queue a second copy of the same week. */
  const recapTried = useRef(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Pear Plus (card 32BEnNO6I2kx): the plan row. Null until the state call
   *  lands; a load failure leaves it null and the row simply stays out of the
   *  way — Settings must never look broken because billing is unconfigured. */
  const [billing, setBilling] = useState<BillingState | null>(null);

  const load = useCallback(() => {
    api<{ toggles: Toggles }>('/api/ai/toggles')
      .then(data => {
        setToggles(data.toggles ?? {});
        setFailed(false);
      })
      .catch(err => {
        // A page teardown cancels this request; that is not a load failure and
        // must not paint Settings as broken (card d0Md65M0shSI).
        if (isAbort(err)) return;
        console.error('[Pear] settings: toggles load failed', err);
        setFailed(true);
      });
    api<{ profile: Profile; settings: CoupleSettings; handle: string | null }>('/api/settings')
      .then(data => {
        setProfile(data.profile);
        setSettings(data.settings);
        // The chat name seeds from the name captured at pair-creation, so
        // the field is never blank on first open (card yFLCjm7ONIEc).
        if (!chatDirty.current) setChatName(data.profile.displayName ?? '');
        if (!handleDirty.current) {
          setHandle(data.handle ?? null);
          setHandleDraft(null);
        }
      })
      .catch(err => { if (!isAbort(err)) console.error('[Pear] settings load failed', err); });
    // The real name lives on the account, which needs the ACCOUNT session —
    // a couple token cannot reach it. Pairing works without an account, so
    // say why rather than show a field that can never save, and do not fire
    // a request that can only answer 401.
    if (!getAuthSession()) {
      setRealName(null);
      setNameBlocked('Sign in to keep your real name on file. Your display name below works either way.');
    } else {
      authApi<{ account: AccountName }>('/api/auth/me')
        .then(data => {
          if (!nameDirty.current) setRealName(data.account?.name ?? '');
          setNameBlocked(null);
        })
        .catch(err => {
          // Teardown, not a failure — leave the row as it was (card d0Md65M0shSI).
          if (isAbort(err)) return;
          console.error('[Pear] settings: account name load failed', err);
          setRealName(null);
          setNameBlocked(authErrorMessage(err, 'Your name could not be loaded — try again.'));
        });
    }
    api<{ frequency: number }>('/api/digest')
      .then(data => setDigestFreq(Number(data.frequency) || 0))
      .catch(err => { if (!isAbort(err)) console.error('[Pear] digest frequency load failed', err); });
    // Pear Plus (card 32BEnNO6I2kx): the plan row reads the entitlement.
    //  A 401 (no couple token) or an unconfigured server leaves it null.
    api<{ entitlement: { plus: boolean; trialing?: boolean; trialEnd?: number | null; interval: string | null; currentPeriodEnd: number | null; subscriptionId: string | null; promoCode: string | null; promoEnd: number | null }; prices: Record<string, { label: string }>; configured: boolean }>('/api/billing/state')
      .then(data => {
        const e = data.entitlement;
        const price = e.interval ? data.prices[e.interval] : undefined;
        // A trial is Plus without a subscription (card ImIQwJDLW6Xs). It has
        // no Stripe customer yet, so "Manage" would open a portal that 404s —
        // the trial row offers Upgrade instead, and names its own end date.
        const trialing = Boolean(e.trialing);
        // A promo grant is the same trap for a different reason (card
        // Z7SGpA59WHSb): it is Plus, but it was GIVEN, so the couple has no
        // Stripe customer and no subscription to manage. Presenting it as a
        // subscription ("Renews <date>" + Manage) both misstates what they
        // have and dead-ends the portal. Name it as the grant it is.
        // It is checked BEFORE trialing because a granted couple still has a
        // live trialEnd (the trial clock keeps running underneath the grant),
        // so the server reports trialing: true — and "free trial" would be a
        // worse description of an indefinite grant than of a 30-day trial.
        const granted = Boolean(e.promoCode) && !e.subscriptionId;
        setBilling({
          plus: Boolean(e.plus),
          trialing,
          granted,
          label: granted
            ? 'Pear Plus · granted'
            : trialing
              ? 'Pear Plus · free trial'
              : e.plus
                ? `Pear Plus${price ? ` · ${price.label}` : ''}`
                : 'Pear Essentials',
          note: granted
            ? e.promoEnd
              ? `Granted to you until ${new Date(e.promoEnd).toLocaleDateString()} — nothing to pay, and no subscription to manage.`
              : 'Granted to you — nothing to pay, and no subscription to manage.'
            : trialing
              ? e.trialEnd
                ? `Free trial until ${new Date(e.trialEnd).toLocaleDateString()} — one subscription then covers you both.`
                : 'Free trial — one subscription then covers you both.'
              : e.plus
                ? e.currentPeriodEnd
                  ? `Renews ${new Date(e.currentPeriodEnd).toLocaleDateString()} — one subscription covers you both.`
                  : 'One subscription covers you both.'
                : 'The concierge services and weekly briefing are part of Pear Plus.',
          trialEnd: e.trialEnd ?? null,
          periodEnd: e.currentPeriodEnd ?? null,
          configured: Boolean(data.configured),
        });
      })
      .catch(err => { if (!isAbort(err)) console.error('[Pear] billing state load failed', err); });
    api<{ enabled: boolean; due: boolean; lastSentAt: number; nextDueAt: number }>('/api/recap')
      .then(data => {
        const next = {
          enabled: Boolean(data.enabled),
          due: Boolean(data.due),
          lastSentAt: Number(data.lastSentAt) || 0,
          nextDueAt: Number(data.nextDueAt) || 0,
        };
        setRecap(next);
        // The cadence: opted in and the week has run → send it now, once.
        if (next.enabled && next.due && !recapTried.current) sendRecap();
      })
      .catch(err => { if (!isAbort(err)) console.error('[Pear] recap load failed', err); });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (which: 'profile' | 'joint', body: Record<string, unknown>) => {
      setSaving(which);
      setSaveError(null);
      try {
        if (which === 'profile') {
          const data = await api<{ profile: Profile }>('/api/settings/profile', {
            method: 'PUT',
            body,
          });
          setProfile(data.profile);
        } else {
          const data = await api<{ settings: CoupleSettings }>('/api/settings/joint', {
            method: 'PUT',
            body,
          });
          setSettings(data.settings);
        }
      } catch (err) {
        console.error('[Pear] settings save failed', err);
        setSaveError('Couldn’t save that. Try again.');
        load();
      } finally {
        setSaving(null);
      }
    },
    [load],
  );

  /**
   * The two name fields autosave per keystroke (card yFLCjm7ONIEc). When the
   * server rejects a value, the typed text STAYS in the field: the server's
   * own sentence lands in the status line and the field takes focus, so a
   * failed save is never a silent rollback. Each field keeps its own request
   * counter so a slow response cannot repaint a newer keystroke.
   */
  const failName = (field: 'set-name' | 'set-display-name', message: string) => {
    setSaving(null);
    setSaveError(message);
    document.getElementById(field)?.focus();
  };

  const saveName = async (value: string): Promise<void> => {
    const seq = ++nameSeq.current;
    nameDirty.current = true;
    setRealName(value);
    setSaving('name');
    setSaveError(null);
    try {
      const data = await authApi<{ account: AccountName }>('/api/auth/profile', {
        method: 'PUT',
        body: { name: value },
      });
      if (seq !== nameSeq.current) return; // a newer keystroke owns the field
      setRealName(data.account?.name ?? value);
      setSaving(null);
    } catch (err) {
      if (seq !== nameSeq.current) return;
      console.error('[Pear] name save failed', err);
      failName('set-name', authErrorMessage(err, 'Couldn’t save that. Try again.'));
    }
  };

  const saveChatName = async (value: string): Promise<void> => {
    const seq = ++chatSeq.current;
    chatDirty.current = true;
    setChatName(value);
    setSaving('display');
    setSaveError(null);
    try {
      const data = await api<{ profile: Profile }>('/api/settings/profile', {
        method: 'PUT',
        body: { displayName: value },
      });
      if (seq !== chatSeq.current) return;
      const saved = data.profile?.displayName ?? value;
      setChatName(saved);
      setProfile(p => (p ? { ...p, displayName: saved } : p));
      setSaving(null);
    } catch (err) {
      if (seq !== chatSeq.current) return;
      console.error('[Pear] display name save failed', err);
      failName('set-display-name', authErrorMessage(err, 'Couldn’t save that. Try again.'));
    }
  };

  /** The handle saves on blur or Enter, not per keystroke: uniqueness is
   *  checked once, against the whole table, and a rejection must read as
   *  "taken", not as a typo. The typed text stays in the field on failure and
   *  the server's sentence lands in the status line (PRD §6 — the words are
   *  the reason). A successful save shows the normalised value back, so a
   *  person who typed "Velvet Moon" sees "velvet-moon". */
  const commitHandle = async (): Promise<void> => {
    const value = (handleDraft ?? handle ?? '').trim();
    if (!handle || value === handle) {
      handleDirty.current = false;
      setHandleDraft(null);
      return;
    }
    setSaving('handle');
    setSaveError(null);
    try {
      const data = await api<{ handle: string }>('/api/settings/handle', {
        method: 'PUT',
        body: { handle: value },
      });
      handleDirty.current = false;
      setHandle(data.handle);
      setHandleDraft(null);
      setSaving(null);
    } catch (err) {
      console.error('[Pear] handle save failed', err);
      setSaving(null);
      setSaveError(authErrorMessage(err, 'Couldn’t save that. Try again.'));
      document.getElementById('set-handle')?.focus();
    }
  };

  /** One PUT, optimistic like every other row: frequency is a small integer
   *  the DO validates, so the control can move before the round trip lands. */
  const saveDigestFrequency = async (frequency: number) => {
    const value = Math.max(0, Math.min(7, Math.round(frequency)));
    setDigestFreq(value);
    setSaving('digest');
    setSaveError(null);
    try {
      const data = await api<{ frequency: number }>('/api/digest/frequency', {
        method: 'PUT',
        body: { frequency: value },
      });
      setDigestFreq(data.frequency);
    } catch (err) {
      console.error('[Pear] digest frequency save failed', err);
      setSaveError('Couldn’t save that. Try again.');
      load();
    } finally {
      setSaving(null);
    }
  };

  /** Weekly recap (card 3UwJvUtiirGN): the 'recap' permission doubles as the
   *  opt-in, so this row writes the same toggle the concierge screen writes —
   *  one writer, no second table to drift. */
  const setRecapEnabled = async (on: boolean) => {
    setRecap(prev => (prev ? { ...prev, enabled: on } : { enabled: on, due: false, lastSentAt: 0, nextDueAt: 0 }));
    setSaving('recap');
    setSaveError(null);
    setRecapNote(null);
    try {
      await api<{ toggles: Toggles }>('/api/ai/toggles', { method: 'PUT', body: { category: 'recap', on } });
      const state = await api<{ enabled: boolean; due: boolean; lastSentAt: number; nextDueAt: number }>('/api/recap');
      setRecap({ enabled: state.enabled, due: state.due, lastSentAt: state.lastSentAt || 0, nextDueAt: state.nextDueAt || 0 });
      // Turning it on when the week has already run sends that week — the
      // window is not something the user should have to wait out twice.
      if (state.enabled && state.due && !recapTried.current) void sendRecap();
    } catch (err) {
      console.error('[Pear] recap toggle save failed', err);
      setSaveError('Couldn’t save that. Try again.');
      load();
    } finally {
      setSaving(null);
    }
  };

  /** The send itself. Called at most once per mount (recapTried), and the
   *  server refuses anything that is off, not due or empty — so the row can
   *  only ever report an honest reason. */
  const sendRecap = async () => {
    if (recapTried.current) return;
    recapTried.current = true;
    // What the server said before we asked, so a failure below can be
    // checked against what actually happened (card myEbyq9tpkJS).
    const lastSentBefore = recap?.lastSentAt ?? 0;
    setRecapNote('Sending this week’s recap…');
    try {
      const data = await api<{
        sent: boolean;
        reason: string;
        deliveries: { to: string; name?: string | null; sent: boolean; reason?: string }[];
      }>('/api/recap/send', { method: 'POST', body: {} });
      if (data.sent) {
        setRecap(prev => (prev ? { ...prev, due: false, lastSentAt: Date.now() } : prev));
        const rows = data.deliveries ?? [];
        const who = (d: { to: string; name?: string | null }) => d.name || d.to;
        const gone = rows.filter(d => d.sent).map(who);
        // A partner who did not get theirs is named — silence would read as
        // "everyone got it", which is exactly the lie this row must not tell.
        const missed = rows.filter(d => !d.sent && d.reason !== 'nothing_to_report').map(who);
        setRecapNote(
          missed.length
            ? `Sent to ${gone.join(', ')} — missed ${missed.join(', ')}.`
            : gone.length
              ? `Sent — ${gone.join(', ')}`
              : 'Sent.',
        );
      } else {
        setRecapNote(recapReason(data.reason));
      }
    } catch (err) {
      console.error('[Pear] recap send failed', err);
      // The mail may already be out: a non-2xx after delivery must never be
      // reported as a failure, so ask the server what it recorded before
      // claiming the send did not go (msg_6b9579f2).
      try {
        const st = await api<{ enabled: boolean; due: boolean; lastSentAt: number; nextDueAt: number }>('/api/recap');
        if (Number(st.lastSentAt) > lastSentBefore) {
          setRecap({ enabled: Boolean(st.enabled), due: Boolean(st.due), lastSentAt: Number(st.lastSentAt), nextDueAt: Number(st.nextDueAt) || 0 });
          setRecapNote('Sent — your recap is on its way.');
          return;
        }
      } catch {
        // No answer either way — fall through to the honest failure line.
      }
      recapTried.current = false; // a transport error may be transient
      setRecapNote('Couldn’t send that. Try again later.');
    }
  };

  const patchHolidays = (next: Holiday[]) => {
    if (!settings) return;
    setSaveError(null); // a rejected toggle's message should not outlive the next edit
    setSettings({ ...settings, holidays: next });
    void save('joint', { holidays: next });
  };

  /** Clamp 1-365 exactly as the input always has, then save through the same
   *  path — the stepper and preset chips are just other ways to pick a number. */
  const setLead = (index: number, raw: number): void => {
    if (!settings) return;
    const leadDays = Math.max(1, Math.min(365, Math.round(raw)));
    const next = [...settings.holidays];
    next[index] = { ...next[index], leadDays };
    patchHolidays(next);
  };

  const customCount = settings ? settings.holidays.filter(h => isCustomKey(h.key)).length : 0;

  const addCustom = () => {
    if (!settings || customCount >= MAX_CUSTOM) return;
    patchHolidays([
      ...settings.holidays,
      { key: `custom-${customCount + 1}`, label: '', enabled: false, leadDays: 30, date: null },
    ]);
  };

  const status = leaveError ? leaveError : saveError ? saveError : saving ? 'Saving…' : '';

  /**
   * Card Vf23eSKKxDhX — the confirm tap, and the whole reason Leave had to
   * change. It used to clear browser storage and call it done, which left
   * `accounts.couple_id` pointing at the room: the next sign-in pulled the
   * same person straight back in. Now the server goes first, and a refusal
   * leaves everything exactly as it was — screen open, couple intact, reason
   * in the status line above. A Leave that half-happens is worse than one
   * that did not.
   */
  const confirmLeave = async (): Promise<void> => {
    if (leaving) return;
    setLeaving(true);
    setLeaveError(null);
    const failed = await onLeave();
    // Success unmounts this screen (App closes it), so the state below only
    // ever runs on the refusal path.
    setLeaving(false);
    if (failed) setLeaveError(failed);
  };

  /** Card ocJz1TeLT4pi — fetch this member's private half as a bundle and
   *  save it as a download, before Leave is confirmed. Failure (out of
   *  couple, server down) reports in the confirm sheet instead of blocking
   *  the leave itself — the export is an offer, never a gate. */
  const downloadMyData = async (): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    setExportMsg(null);
    try {
      const bundle = await exportMeRequest();
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pear-my-data.json';
      a.click();
      URL.revokeObjectURL(url);
      setExportMsg('Your bundle is downloading — keep it, then leave whenever you are ready.');
    } catch (e) {
      setExportMsg(authErrorMessage(e, 'Could not build your bundle — try again.'));
    } finally {
      setExporting(false);
    }
  };

  /** Section navigation (card DI31pLXCFc2C): a tap on a tab switches the
   *  section — the tab row IS the navigation, pinned while content scrolls.
   *  The hash follows so the URL is always a shareable link to what shows. */
  const selectTab = useCallback((key: string) => {
    onTabChange(key);
    if (window.location.hash !== `#settings-${key}`) {
      window.history.replaceState(null, '', `#settings-${key}`);
    }
    // Land the section at the top of the scrollport once it mounts.
    window.requestAnimationFrame(() => {
      document.getElementById('settings-screen')?.scrollIntoView({ block: 'start' });
    });
  }, [onTabChange]);

  return (
    <div className="set-panel" id="settings-screen">
      <div className="set-head">
        <h2 className="set-title">Settings</h2>
        <div className="set-head-actions">
          <button
            type="button"
            className="pear-btn set-done"
            id="btn-settings-done"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>

      <span className="set-status" aria-live="polite">{status}</span>

      <SubTabs
        name="settings"
        label="Settings sections"
        tabs={SETTINGS_TABS}
        active={tab}
        onChange={selectTab}
        sticky
      />
      <SubPanel name="settings" tabKey={tab}>
        {tab === 'you' && (
          <YouSection
            awaitingPartner={awaitingPartner}
            pendingCode={pendingCode}
            mailEnabled={mailEnabled}
            nameBlocked={nameBlocked}
            realName={realName}
            saveName={saveName}
            chatName={chatName}
            saveChatName={saveChatName}
            profile={profile}
            setProfile={setProfile}
            save={save}
            digestFreq={digestFreq}
            saveDigestFrequency={saveDigestFrequency}
            recap={recap}
            recapNote={recapNote}
            saving={saving}
            setRecapEnabled={setRecapEnabled}
          />
        )}

        {tab === 'us' && (
          <UsSection
            handle={handle}
            handleDraft={handleDraft}
            setHandleDraft={setHandleDraft}
            handleDirty={handleDirty}
            commitHandle={commitHandle}
            settings={settings}
            setSettings={setSettings}
            save={save}
            saving={saving}
            setSaveError={setSaveError}
            patchHolidays={patchHolidays}
            setLead={setLead}
            customCount={customCount}
            addCustom={addCustom}
          />
        )}

        {tab === 'concierge' && (
          <ConciergeSection
            onOpenAI={onOpenAI}
            toggles={toggles}
            failed={failed}
          />
        )}

        {tab === 'plan' && <PlanSection billing={billing} />}

        {tab === 'about' && (
          <AboutSection
            onOpenHelp={onOpenHelp}
            onOpenTour={onOpenTour}
            onSignOut={onSignOut}
            confirming={confirming}
            setConfirming={setConfirming}
            setLeaveError={setLeaveError}
            leaving={leaving}
            confirmLeave={confirmLeave}
            exporting={exporting}
            exportMsg={exportMsg}
            downloadMyData={downloadMyData}
          />
        )}
      </SubPanel>

    </div>
  );
}
